import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Fade,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, Replay, Videocam } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getStoredToken,
  registerFaceBase64,
  verifyFaceQualityBase64,
} from '../utils/api';
import CapturePreviewCard from './employeeEnrollment/CapturePreviewCard';
import EnrollmentStepper from './employeeEnrollment/EnrollmentStepper';
import WebcamCapture from './employeeEnrollment/WebcamCapture';

const ENROLLMENT_STEPS = [
  { key: 'front', label: 'Front', direction: 'front', required: true },
  // Profile-side capture uses opposite head-turn direction:
  // left profile => turn right, right profile => turn left.
  { key: 'left', label: 'Left', direction: 'right', required: true },
  { key: 'right', label: 'Right', direction: 'left', required: true },
  { key: 'custom', label: 'Top View', direction: 'top', required: false },
];

const VISITOR_EMPLOYEE_TYPE = { is_visitor: true };
const REQUIRED_KEYS = ENROLLMENT_STEPS.filter((step) => step.required).map((step) => step.key);
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const VISITOR_NAME_MAX_LENGTH = 255;
const ALPHANUMERIC_REGEX = /^[A-Za-z0-9]+$/;
const IDENTITY_SIMILARITY_THRESHOLD = 0.38;

const EMPTY_QUALITY_RESULTS = {
  front: null,
  left: null,
  right: null,
  custom: null,
};

const EMPTY_CAPTURED_IMAGES = {
  front: null,
  left: null,
  right: null,
  custom: null,
};

const VisitorRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [comment, setComment] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [visitorId, setVisitorId] = useState('');
  const [visitorIdError, setVisitorIdError] = useState(false);
  const [capturedImages, setCapturedImages] = useState(EMPTY_CAPTURED_IMAGES);
  const [qualityResults, setQualityResults] = useState(EMPTY_QUALITY_RESULTS);
  const [isEnrollmentActive, setIsEnrollmentActive] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [captureSessionNonce, setCaptureSessionNonce] = useState(0);
  const [cameraError, setCameraError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const capturedImagesRef = useRef(EMPTY_CAPTURED_IMAGES);
  const captureOrderRef = useRef([]);
  const visitorIdInputRef = useRef(null);
  const previewFrameRefs = useRef({});
  const prefillAppliedRef = useRef(false);

  const currentStep = ENROLLMENT_STEPS[activeStep] || ENROLLMENT_STEPS[0];
  const isRedirectRegistration = location?.state?.source === 'audit_train';
  const requiredKeys = isRedirectRegistration ? ['front'] : REQUIRED_KEYS;
  const requiredCaptured = requiredKeys.every((key) => Boolean(capturedImages[key]));

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const firstMissingRequiredStepIndex = useMemo(() => {
    const missing = ENROLLMENT_STEPS.findIndex(
      (step) => {
        const isRequired = isRedirectRegistration ? step.key === 'front' : step.required;
        return isRequired && !capturedImages[step.key];
      }
    );
    return missing >= 0 ? missing : ENROLLMENT_STEPS.length - 1;
  }, [capturedImages, isRedirectRegistration]);

  const isStepSelectable = (index) => {
    const step = ENROLLMENT_STEPS[index];
    if (!step) return false;
    if (capturedImages[step.key]) return true;
    return index <= firstMissingRequiredStepIndex;
  };

  const startEnrollment = (preferredStepIndex = null) => {
    const stepToStart =
      preferredStepIndex !== null
        ? preferredStepIndex
        : ENROLLMENT_STEPS.findIndex((step) => {
            const isRequired = isRedirectRegistration ? step.key === 'front' : step.required;
            return isRequired && !capturedImages[step.key];
          });

    setActiveStep(stepToStart >= 0 ? stepToStart : ENROLLMENT_STEPS.length - 1);
    setCameraError('');
    setIsEnrollmentActive(true);
  };

  const stopEnrollment = () => {
    setIsEnrollmentActive(false);
  };

  const restartCurrentCaptureSession = () => {
    setCaptureSessionNonce((previous) => previous + 1);
  };

  const markStepCaptured = (stepKey) => {
    captureOrderRef.current = [
      ...captureOrderRef.current.filter((key) => key !== stepKey),
      stepKey,
    ];
  };

  const removeStepFromCaptureOrder = (stepKey) => {
    captureOrderRef.current = captureOrderRef.current.filter((key) => key !== stepKey);
  };

  const getLastCapturedReferenceImage = (excludeStepKey = null) => {
    const order = captureOrderRef.current;
    const currentCaptured = capturedImagesRef.current;
    for (let index = order.length - 1; index >= 0; index -= 1) {
      const key = order[index];
      if (excludeStepKey && key === excludeStepKey) continue;
      if (currentCaptured[key]) {
        return currentCaptured[key];
      }
    }
    return null;
  };

  const handleRetake = (stepKey) => {
    const stepIndex = ENROLLMENT_STEPS.findIndex((step) => step.key === stepKey);
    if (stepIndex < 0) return;

    setCapturedImages((previous) => ({
      ...previous,
      [stepKey]: null,
    }));
    setQualityResults((previous) => ({
      ...previous,
      [stepKey]: null,
    }));
    removeStepFromCaptureOrder(stepKey);
    startEnrollment(stepIndex);
  };

  const handleRetakeAll = () => {
    setCapturedImages(EMPTY_CAPTURED_IMAGES);
    setQualityResults(EMPTY_QUALITY_RESULTS);
    captureOrderRef.current = [];
    setActiveStep(0);
    setIsEnrollmentActive(false);
    showSnackbar('All captured photos cleared. Start enrollment again.', 'info');
  };

  const verifyImageQuality = async (stepKey, imageBase64, referenceImageBase64 = null) => {
    if (!stepKey || !imageBase64) return null;

    setQualityResults((previous) => ({
      ...previous,
      [stepKey]: {
        loading: true,
        success: false,
        message: 'Checking image quality...',
      },
    }));

    try {
      const result = await verifyFaceQualityBase64(
        imageBase64,
        referenceImageBase64,
        referenceImageBase64 ? IDENTITY_SIMILARITY_THRESHOLD : null
      );
      setQualityResults((previous) => ({
        ...previous,
        [stepKey]: {
          ...result,
          loading: false,
        },
      }));
      return result;
    } catch (error) {
      console.error('Image quality verification failed:', error);
      const errorResult = {
        loading: false,
        success: false,
        face_detected: false,
        face_count: 0,
        message: error.message || 'Failed to verify image quality',
        angle: null,
        pose_quality: 0,
        lighting_quality: 0,
        quality_score: 0,
        similarity: null,
        similarity_threshold: referenceImageBase64 ? IDENTITY_SIMILARITY_THRESHOLD : null,
        is_same_person: null,
        encoding_dimension: 0,
        encoding_preview: [],
      };
      setQualityResults((previous) => ({
        ...previous,
        [stepKey]: errorResult,
      }));
      return errorResult;
    }
  };

  const handleCaptured = async (imageBase64) => {
    const capturedStep = currentStep;
    const capturedStepIndex = activeStep;
    const referenceImageBase64 = getLastCapturedReferenceImage(capturedStep.key);
    const verification = await verifyImageQuality(
      capturedStep.key,
      imageBase64,
      referenceImageBase64
    );

    if (!verification?.success) {
      showSnackbar(
        verification?.message || 'Image rejected. Please capture the same person only.',
        'error'
      );
      restartCurrentCaptureSession();
      return;
    }

    setCapturedImages((previous) => ({
      ...previous,
      [capturedStep.key]: imageBase64,
    }));
    markStepCaptured(capturedStep.key);

    if (capturedStep.key === 'right') {
      showSnackbar('Right angle captured. Top View is optional and can be captured now.', 'success');
      setActiveStep(3);
      return;
    }

    if (capturedStep.key === 'custom') {
      showSnackbar('Top View captured.', 'success');
      setIsEnrollmentActive(false);
      return;
    }

    const nextStepIndex = Math.min(capturedStepIndex + 1, ENROLLMENT_STEPS.length - 1);
    setActiveStep(nextStepIndex);
    showSnackbar(`${capturedStep.label} captured.`, 'success');
  };

  const handleManualUpload = (stepKey) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      showSnackbar('Please choose a valid image file.', 'error');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      showSnackbar('Image is too large. Maximum size is 5MB.', 'error');
      return;
    }

    try {
      const stepLabel = ENROLLMENT_STEPS.find((step) => step.key === stepKey)?.label || 'Image';
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const referenceImageBase64 = getLastCapturedReferenceImage(stepKey);
      const verification = await verifyImageQuality(stepKey, base64, referenceImageBase64);
      if (!verification?.success) {
        showSnackbar(
          verification?.message || 'Uploaded image does not match previously captured person.',
          'error'
        );
        restartCurrentCaptureSession();
        return;
      }

      setCapturedImages((previous) => ({
        ...previous,
        [stepKey]: base64,
      }));
      markStepCaptured(stepKey);
      showSnackbar(`${stepLabel} uploaded successfully.`, 'success');
    } catch (error) {
      console.error('Manual upload error:', error);
      showSnackbar('Unable to read selected image. Please try another file.', 'error');
    }
  };

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const prefillCaptureImage = location?.state?.prefillCaptureImage;
    if (!prefillCaptureImage || typeof prefillCaptureImage !== 'string') return;
    prefillAppliedRef.current = true;
    setCapturedImages((previous) => ({
      ...previous,
      front: prefillCaptureImage,
    }));
    setQualityResults((previous) => ({
      ...previous,
      front: previous.front || {
        loading: false,
        success: true,
        message: 'Prefilled from audit capture.',
      },
    }));
    markStepCaptured('front');
    if (activeStep === 0) setActiveStep(isRedirectRegistration ? 0 : 1);
  }, [activeStep, isRedirectRegistration, location?.state]);

  useEffect(() => {
    capturedImagesRef.current = capturedImages;
  }, [capturedImages]);

  const registerVisitor = async () => {
    const trimmedEmployeeName = employeeName.trim();
    const resolvedPersonId = visitorId.trim();

    if (!trimmedEmployeeName || !resolvedPersonId) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    if (!ALPHANUMERIC_REGEX.test(trimmedEmployeeName)) {
      showSnackbar(
        'Visitor Name can contain only letters and numbers. Spaces and special characters are not allowed.',
        'error'
      );
      return;
    }

    if (!ALPHANUMERIC_REGEX.test(resolvedPersonId)) {
      showSnackbar(
        'Visitor ID can contain only letters and numbers. Spaces and special characters are not allowed.',
        'error'
      );
      return;
    }
    if (trimmedEmployeeName.length > VISITOR_NAME_MAX_LENGTH) {
      showSnackbar(`Visitor Name must be at most ${VISITOR_NAME_MAX_LENGTH} characters`, 'error');
      return;
    }
    if (!requiredCaptured) {
      showSnackbar(
        isRedirectRegistration
          ? 'Please complete Front capture'
          : 'Please complete Front, Left and Right captures',
        'error'
      );
      return;
    }

    setIsRegistering(true);
    try {
      const results = [];
      const registrationPayloads = [
        { key: 'front', angle: 'front', label: null },
      ];

      if (capturedImages.left) {
        registrationPayloads.push({ key: 'left', angle: 'left', label: null });
      }

      if (capturedImages.right) {
        registrationPayloads.push({ key: 'right', angle: 'right', label: null });
      }

      if (capturedImages.custom) {
        registrationPayloads.push({ key: 'custom', angle: 'custom_angle', label: 'Top View' });
      }

      for (const payload of registrationPayloads) {
        const result = await registerFaceBase64(
          trimmedEmployeeName,
          resolvedPersonId,
          capturedImages[payload.key],
          'jpeg',
          payload.angle,
          payload.label,
          null,
          null,
          false,
          null,
          null,
          null,
          VISITOR_EMPLOYEE_TYPE,
          null,
          comment
        );
        results.push(result);
      }

      const isOk = (result) => (
        Boolean(result) &&
        (result.success || /already exists with better quality/i.test(result.message || ''))
      );
      const failed = results.filter((result) => !isOk(result));
      if (failed.length) {
        showSnackbar(failed[0]?.message || 'One or more angles could not be registered.', 'error');
        return;
      }

      setComment('');
      setEmployeeName('');
      setVisitorId('');
      setCapturedImages(EMPTY_CAPTURED_IMAGES);
      setQualityResults(EMPTY_QUALITY_RESULTS);
      captureOrderRef.current = [];
      setIsEnrollmentActive(false);
      setActiveStep(0);

      navigate('/visitors', {
        state: {
          toast: {
            message: 'Visitor registered with guided face enrollment successfully!',
            severity: 'success',
          },
        },
      });
    } catch (error) {
      console.error('Error registering visitor:', error);

      if (error?.status === 409 && error?.detail?.code === 'FACE_ANGLE_EXISTS') {
        setVisitorIdError(true);

  setTimeout(() => {
    visitorIdInputRef.current?.focus();
  }, 0);
        showSnackbar(
          error.message || 'This visitor already has a face at this angle. Remove it in Visitor Management or pick another angle.',
          'warning'
        );
        return;
      }

      let userMessage = error.message || 'Failed to register visitor. Please try again.';

      if (error.message) {
        if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
          userMessage = 'Registration request timed out. Please check your connection and try again.';
        } else if (
          error.message.includes('Authentication expired') ||
          error.message.includes('No authentication token')
        ) {
          userMessage = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          userMessage = 'Network error. Please check your internet connection.';
        } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          userMessage = 'A visitor with this ID already exists. Please use a different ID.';
        } else if (error.message.includes('No face detected')) {
          userMessage = 'No face detected in captured image. Please retake a clear photo.';
        } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
          userMessage = 'Invalid data provided. Please check all fields and try again.';
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          userMessage = 'Server error occurred. Please try again later.';
        }
      }

      showSnackbar(userMessage, 'error');
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, pb: { xs: 2, sm: 4 }, background: '#fafbfc', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
          <Typography
            variant="h4"
            fontWeight={600}
            gutterBottom
            sx={{ mb: 0.5, fontSize: { xs: '1.55rem', sm: '2.15rem' }, lineHeight: 1.2 }}
          >
            Register New Visitor
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: { xs: 2, sm: 2.5 }, fontSize: { xs: '0.88rem', sm: '0.95rem' } }}>
            Guided webcam enrollment captures face angles automatically, with manual upload fallback.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: { xs: 1.5, sm: 3 },
              mb: 2,
            }}
          >
            <TextField
              label="Visitor Name"
              value={employeeName}
              onChange={(event) => setEmployeeName(event.target.value.slice(0, VISITOR_NAME_MAX_LENGTH))}
              required
              placeholder="Visitor Name"
              sx={{ background: '#fff', borderRadius: 2 }}
              InputLabelProps={{ sx: { fontWeight: 500, fontSize: 15 } }}
              inputProps={{ maxLength: VISITOR_NAME_MAX_LENGTH, style: { fontSize: 15 } }}
            />
            <TextField
              label="Visitor ID"
              value={visitorId}
              onChange={(event) => {
              setVisitorId(event.target.value);
              setVisitorIdError(false);
            }}
            required
            error={visitorIdError}
            placeholder="Visitor ID"
              sx={{ background: '#fff', borderRadius: 2 }}
              InputLabelProps={{ sx: { fontWeight: 500, fontSize: 15 } }}
              inputProps={{ maxLength: 100, style: { fontSize: 15 } }}
              inputRef={visitorIdInputRef}
            />
           <TextField
              label="Comment"
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 200))}
              required
              placeholder="Comment"
              multiline
              rows={3}
              sx={{
                background: '#fff',
                borderRadius: 2,
                gridColumn: { xs: 'auto', sm: '1 / -1' },
              }}
              InputLabelProps={{
                sx: { fontWeight: 500, fontSize: 15 },
              }}
              inputProps={{
                maxLength: 200,
                style: { fontSize: 15 },
              }}
              helperText={`${comment.length} / 200`}
            />
          </Box>

          <Box sx={{ background: '#fff', borderRadius: 3, p: { xs: 1.5, sm: 2, md: 3 }, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Face Enrollment
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Chip
                  color={requiredCaptured ? 'success' : 'warning'}
                  label={requiredCaptured ? 'Required Angles Ready' : (isRedirectRegistration ? 'Pending Front Capture' : 'Pending Required Angles')}
                  size="small"
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
                <Button
                  variant="outlined"
                  onClick={handleRetakeAll}
                  disabled={!Object.values(capturedImages).some(Boolean)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Retake All
                </Button>
                {!isEnrollmentActive ? (
                  <Button
                    variant="contained"
                    startIcon={<Videocam />}
                    onClick={() => startEnrollment()}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Start Enrollment
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Replay />}
                    onClick={stopEnrollment}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Pause Camera
                  </Button>
                )}
              </Box>
            </Box>

            {cameraError ? <Alert severity="error" sx={{ mb: 2 }}>{cameraError}</Alert> : null}

            <EnrollmentStepper
              steps={ENROLLMENT_STEPS}
              activeStep={activeStep}
              capturedMap={capturedImages}
              onStepSelect={(index) => {
                if (!isStepSelectable(index)) {
                  showSnackbar('Complete current required steps first.', 'warning');
                  return;
                }
                setActiveStep(index);
                if (!isEnrollmentActive) {
                  startEnrollment(index);
                }
              }}
            />

            <Collapse
              in={isEnrollmentActive}
              timeout={{ enter: 420, exit: 280 }}
              easing={{ enter: 'cubic-bezier(0.2, 0, 0, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
              unmountOnExit
            >
              <Fade in={isEnrollmentActive} timeout={{ enter: 360, exit: 180 }}>
                <Box sx={{ mt: 2 }}>
                  <WebcamCapture
                    key={`${currentStep.key}-${activeStep}-${captureSessionNonce}`}
                    stepKey={currentStep.key}
                    expectedDirection={currentStep.direction}
                    onCaptured={handleCaptured}
                    onPermissionDenied={setCameraError}
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Live guidance validates face presence, centering, distance, head direction, and overlays face mesh.
                      For profile steps: left profile asks you to turn right, right profile asks you to turn left.
                      For Top View, tilt your head down slightly.
                    </Typography>
                    {currentStep.key === 'custom' ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setIsEnrollmentActive(false);
                          showSnackbar('Top View skipped.', 'info');
                        }}
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      >
                        Skip Optional Step
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Fade>
            </Collapse>

            <Box
              sx={{
                mt: 2.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {ENROLLMENT_STEPS.map((step) => (
                <CapturePreviewCard
                  key={step.key}
                  stepKey={step.key}
                  title={step.label}
                  required={isRedirectRegistration ? step.key === 'front' : step.required}
                  image={capturedImages[step.key]}
                  previewFrameRef={(node) => {
                    previewFrameRefs.current[step.key] = node;
                  }}
                  onCapture={() => startEnrollment(ENROLLMENT_STEPS.findIndex((entry) => entry.key === step.key))}
                  onRetake={() => handleRetake(step.key)}
                  onUpload={handleManualUpload(step.key)}
                  qualityResult={qualityResults[step.key]}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              onClick={registerVisitor}
              disabled={
                isRegistering ||
                !employeeName.trim() ||
                !visitorId.trim() ||
                !requiredCaptured
              }
              sx={{ width: { xs: '100%', sm: 260 }, fontWeight: 700, fontSize: 15 }}
              startIcon={
                isRegistering ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutline />
              }
            >
              {isRegistering ? 'Registering...' : 'Register Visitor'}
            </Button>
          </Box>

          {Object.entries(capturedImages).some(([, image]) => Boolean(image)) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Captures are currently stored in-memory as base64 and submitted to the existing enrollment API.
            </Typography>
          ) : null}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default VisitorRegistration;